import React from 'react'
import { Icon, Button } from 'react-bulma-components'
import { Column } from 'react-table'
import Table from '../../components/table'
import useFilters from '../../hooks/filters'
import { Clocking } from '../../api/clockings'
import { QrcodeIcon } from '@heroicons/react/solid'
import { Link } from 'react-router-dom'
import { ClockingStatusEnum, getStatus } from '../../utils/clockings'
import Protected from '../../components/protected/protected'
import useStore from '../../store'
import { useClockingsQuery } from '../../queries/clockings'
import { AppUserRole } from '../../store/session'
import { User } from '../../api/users'
import { getUserLabel } from '../../utils/users'
import ClockingsFilters from '../../components/clockings/filters'
import { FiltersResourcesEnum } from '../../store/filters'
import WorkerLabel from '../../components/texts/worker-label'
import ListPageLayout from '../../components/layout/list-page-layout'
import { getDocIdAsString } from '../../utils/documents'
import { formatTime, formatCompleteDate, localDate } from '../../utils/date'
import { upperFirst } from 'lodash'
import WorkPeriodLink from '../../components/work-periods/work-period-link'
import CreateWorkPeriodFromClockingsModal from '../../components/clockings/create-work-period-from-clockings-modal'

const ClockingsList: React.FunctionComponent = () => {
  const { filters } = useFilters(FiltersResourcesEnum.clockings)
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const [triggerClocking, setTriggerClocking] = React.useState<Clocking | null>(null)

  const clockingsQuery = useClockingsQuery(
    {
      limit: 300,
      start: filters.start,
      end: filters.end,
      user: filters.user?.[0] ? filters.user : undefined,
    },
    { refetchInterval: 10000, enabled: Boolean(filters?.start) },
  )

  const clockingsData = (clockingsQuery.data || []).filter(
    c => !filters.type?.length || filters.type.includes(getStatus(c)),
  )

  const tableColumns = useListTableColumns(clockingsData, currentUserRole, setTriggerClocking)

  const action = (
    <Protected roles={['employerMember']} action={{ resource: 'clockings', name: 'create' }}>
      <Button renderAs={Link} to="/qr-scanner" outlined color="primary" size="small">
        <Icon>
          <QrcodeIcon />
        </Icon>
        <span>Pointer</span>
      </Button>
    </Protected>
  )

  return (
    <ListPageLayout
      title="Liste des derniers pointages"
      subtitle="Affiche les pointages les plus récents"
      action={action}
      filters={<ClockingsFilters />}
    >
      <Table
        columns={tableColumns}
        data={clockingsData}
        noDataMessage="Aucun pointage à afficher"
        dataTest="clockings-table"
      />
      {triggerClocking && (
        <CreateWorkPeriodFromClockingsModal
          show={Boolean(triggerClocking)}
          hide={() => setTriggerClocking(null)}
          triggerClocking={triggerClocking}
        />
      )}
    </ListPageLayout>
  )
}

export default ClockingsList

const useListTableColumns = (
  data: Clocking[],
  currentUserRole: AppUserRole,
  onCreateWorkPeriod: (clocking: Clocking) => void,
): Column[] => {
  return React.useMemo(
    () =>
      [
        currentUserRole !== 'worker' && {
          Header: <WorkerLabel />,
          accessor: 'user',
          Cell: (data: any) => {
            const clocking: Clocking = data.cell.row.original
            const user = clocking.user as User
            return (
              <Link to={`/workers/${getDocIdAsString(user)}`}>
                {getUserLabel(user)}
              </Link>
            )
          },
        },
        {
          Header: 'Jour',
          accessor: 'date',
          Cell: (data: any) => {
            const clocking: Clocking = data.cell.row.original
            return upperFirst(formatCompleteDate(new Date(clocking.date)))
          },
        },
        {
          Header: 'Heure',
          accessor: 'time',
          Cell: (data: any) => {
            const clocking: Clocking = data.cell.row.original
            return formatTime(new Date(clocking.date))
          },
        },
        {
          Header: 'Type',
          accessor: 'entry',
          Cell: (data: any) => {
            const clocking: Clocking = data.cell.row.original
            return getStatus(clocking)
          },
        },
        currentUserRole !== 'worker' && {
          Header: 'Horaire lié au Pointage',
          accessor: 'workPeriod',
          Cell: (data: any) => {
            const clocking: Clocking = data.cell.row.original
            const status = getStatus(clocking)

            // Registered clocking → show WP hours as link
            if (clocking.workPeriod) {
              const wp = clocking.workPeriod
              return (
                <WorkPeriodLink workPeriod={wp} textColor="primary">
                  {formatTime(new Date(wp.start.date))} - {formatTime(new Date(wp.end.date))}
                </WorkPeriodLink>
              )
            }

            // Orphan → "Corriger" button opens context modal
            if (status === ClockingStatusEnum.noMission && clocking.matchingMission) {
              return (
                <Button
                  size="small"
                  color="primary"
                  outlined
                  onClick={() => onCreateWorkPeriod(clocking)}
                  data-test="create-wp-from-clocking"
                >
                  Corriger
                </Button>
              )
            }

            return null
          },
        },
        currentUserRole !== 'worker' && {
          Header: 'Mission',
          accessor: 'mission',
          Cell: (data: any) => {
            const clocking: Clocking = data.cell.row.original
            const mission = clocking.mission ?? clocking.matchingMission
            if (!mission) return null
            return (
              <Link to={`/missions/${getDocIdAsString(mission)}`}>
                {localDate(mission.start)} - {mission.end ? localDate(mission.end) : '/'}
              </Link>
            )
          },
        },

        currentUserRole !== 'employerMember' && {
          Header: 'Employeur',
          accessor: 'employer',
          Cell: (data: any) => {
            const clocking: Clocking = data.cell.row.original
            return clocking?.employer?.name || ''
          },
        },
      ].filter(c => c) as Column[],
    [data, currentUserRole, onCreateWorkPeriod],
  )
}
